import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { fmtPrice } from "@/lib/currency";
import { CheckCircle, XCircle, Clock, Ban, Megaphone, Edit3, RefreshCw } from "lucide-react";
import { ReviewForm } from "@/components/Reviews";
import { minTimeForDate, validateFutureDateTime } from "@/lib/dateConstraints";
import DatePicker from "@/components/ui/DatePicker";

const STATUS_META = {
  pending: { label: "Awaiting vendor", color: "bg-[#F59E0B] text-black", icon: Clock },
  vendor_accepted: { label: "Vendor accepted · awaiting admin", color: "bg-[#06B6D4] text-black", icon: CheckCircle },
  vendor_declined: { label: "Vendor declined · admin reviewing", color: "bg-[#FF3B30] text-white", icon: XCircle },
  confirmed: { label: "Confirmed by Kreeda Nation", color: "bg-[#84CC16] text-black", icon: CheckCircle },
  rejected: { label: "Rejected by Kreeda Nation", color: "bg-[#FF3B30] text-white", icon: XCircle },
  cancelled: { label: "Cancelled", color: "bg-neutral-500 text-white", icon: Ban },
};

/**
 * Renewal nudge card — visible only for subscription-style bookings (per
 * month / per week / per day) whose access period ends within RENEW_WINDOW_DAYS.
 * One-tap POST /vendor-bookings/{id}/renew creates a new booking starting the
 * day after the current one's access ends.
 */
const RENEW_WINDOW_DAYS = 15;

function subscriptionUnit(priceUnit) {
  const u = (priceUnit || "").toLowerCase();
  if (u.includes("month")) return "month";
  if (u.includes("week"))  return "week";
  if (u.includes("day"))   return "day";
  return null;
}

function accessEnd(requestedDate, qty, unit) {
  if (!unit) return null;
  const d = new Date(requestedDate + "T00:00:00");
  if (isNaN(d)) return null;
  const n = Math.max(1, Number(qty) || 1);
  if (unit === "month") d.setMonth(d.getMonth() + n);
  else if (unit === "week") d.setDate(d.getDate() + n * 7);
  else if (unit === "day")  d.setDate(d.getDate() + n);
  d.setDate(d.getDate() - 1);
  return d;
}

function RenewalCard({ booking, onRenewed }) {
  const [busy, setBusy] = useState(false);
  const unit = subscriptionUnit(booking.price_unit);
  const end = unit ? accessEnd(booking.requested_date, booking.hours, unit) : null;
  if (!unit || !end) return null;
  if (["cancelled", "rejected"].includes(booking.status)) return null;
  const daysLeft = Math.ceil((end.getTime() - Date.now()) / (86400000));
  if (daysLeft > RENEW_WINDOW_DAYS || daysLeft < -7) return null;  // hide well outside the window

  const renew = async () => {
    setBusy(true);
    try {
      await api.post(`/vendor-bookings/${booking.id}/renew`);
      toast.success("Renewal request submitted");
      onRenewed && onRenewed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Renewal failed");
    } finally {
      setBusy(false);
    }
  };

  const expired = daysLeft < 0;
  return (
    <div data-testid={`vb-renewal-${booking.id}`}
         className={`mt-3 border rounded-sm p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
           expired ? "border-[#FF3B30]/40 bg-[#FF3B30]/10" : "border-[#FACC15]/40 bg-[#FACC15]/10"
         }`}>
      <div className="text-xs">
        <div className={`font-mono uppercase tracking-widest text-[10px] ${expired ? "text-[#FF3B30]" : "text-[#FACC15]"}`}>
          / {expired ? "Access expired" : `Expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
        </div>
        <div className="text-neutral-200 mt-1">
          Ends {end.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}. Renew now to keep going at ₹{booking.price}/{unit}.
        </div>
      </div>
      <Button data-testid={`vb-renew-btn-${booking.id}`} onClick={renew} disabled={busy}
              className="bg-[#FACC15] hover:bg-[#EAB308] text-black font-semibold rounded-sm whitespace-nowrap">
        <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Renew {booking.hours} {unit}{booking.hours > 1 ? "s" : ""}
      </Button>
    </div>
  );
}

function useVendorBookings() {
  const [bookings, setBookings] = useState([]);
  const reload = () => api.get("/vendor-bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  return [bookings, reload];
}

function StatusBadge({ booking }) {
  const meta = STATUS_META[booking.status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <Badge data-testid={`vb-status-${booking.id}`} className={`${meta.color} text-[10px] font-mono uppercase tracking-widest rounded-sm`}>
      <Icon className="w-3 h-3 mr-1" /> {meta.label}
    </Badge>
  );
}

function NotificationBanner({ booking }) {
  const latest = booking.notifications?.[booking.notifications.length - 1];
  if (!latest) return null;
  return (
    <div className="mt-3 flex items-start gap-2 text-[11px] text-neutral-400 bg-black/30 rounded-sm px-3 py-2 border border-white/5">
      <Megaphone className="w-3 h-3 mt-0.5 text-[#06B6D4]" />
      <div className="min-w-0 flex-1">
        <span className="text-neutral-300">{latest.message}</span>
        <span className="ml-2 text-neutral-600 font-mono uppercase">— {latest.by_name || latest.by_role}</span>
      </div>
    </div>
  );
}

function VendorActions({ booking, onAct }) {
  return (
    <div className="mt-3 flex gap-2">
      <Button data-testid={`vb-vendor-accept-${booking.id}`} size="sm" onClick={() => onAct("vendor_accepted")} className="bg-[#06B6D4] hover:bg-[#0891B2] text-black rounded-sm">Accept</Button>
      <Button data-testid={`vb-vendor-decline-${booking.id}`} size="sm" variant="outline" onClick={() => onAct("vendor_declined")} className="rounded-sm border-white/10 text-white">Decline</Button>
    </div>
  );
}

function HrCancelReschedule({ booking, onCancel, onReschedule }) {
  const [mode, setMode] = useState(null); // null | "cancel" | "reschedule"
  const [date, setDate] = useState(booking.requested_date);
  const [start, setStart] = useState(booking.start_time);
  const [hours, setHours] = useState(booking.hours || 1);
  if (mode === "reschedule") {
    return (
      <div className="mt-3 border-t border-white/5 pt-3 space-y-2">
        <div className="font-mono text-[10px] uppercase text-neutral-500">/ Reschedule booking</div>
        <div className="grid grid-cols-3 gap-2">
          <DatePicker
            testid={`vb-hr-reschedule-date-${booking.id}`}
            value={date}
            onChange={(v) => setDate(v)}
            placeholder="New date"
          />
          <Input type="time" min={minTimeForDate(date)} data-testid={`vb-hr-reschedule-time-${booking.id}`} value={start} onChange={(e) => setStart(e.target.value.slice(0, 5))} className="bg-black/40 border-white/10 text-white text-sm" />
          <Input type="number" min="1" data-testid={`vb-hr-reschedule-hours-${booking.id}`} value={hours} onChange={(e) => setHours(Number(e.target.value) || 1)} className="bg-black/40 border-white/10 text-white text-sm" />
        </div>
        <div className="flex gap-2">
          <Button data-testid={`vb-hr-reschedule-submit-${booking.id}`} size="sm" onClick={() => {
            const err = validateFutureDateTime(date, start);
            if (err) { toast.error(err); return; }
            onReschedule({ requested_date: date, start_time: start, hours });
          }} className="bg-[#06B6D4] hover:bg-[#0891B2] text-black rounded-sm">
            Submit reschedule
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMode(null)} className="text-neutral-400">Close</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-3 flex gap-2">
      <Button data-testid={`vb-hr-cancel-${booking.id}`} size="sm" variant="ghost" onClick={onCancel} className="text-[#FF3B30]">Cancel booking</Button>
      <Button data-testid={`vb-hr-reschedule-${booking.id}`} size="sm" variant="ghost" onClick={() => setMode("reschedule")} className="text-[#06B6D4]">Reschedule</Button>
    </div>
  );
}

function AdminActions({ booking, onConfirm, onReject }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(booking.admin_notes || "");

  if (!open) {
    return (
      <div className="mt-3 border-t border-white/5 pt-3">
        <Button data-testid={`vb-admin-open-${booking.id}`} size="sm" variant="outline" onClick={() => setOpen(true)} className="rounded-sm border-white/10 text-white">
          <Edit3 className="w-3.5 h-3.5 mr-1" /> Confirm / Reject
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-3 border-t border-white/5 pt-3 space-y-2">
      <Input data-testid={`vb-admin-note-${booking.id}`} placeholder="Admin note (visible to HR & vendor)" value={note} onChange={(e) => setNote(e.target.value)} className="bg-black/40 border-white/10 text-white text-sm" />
      <div className="flex gap-2">
        <Button data-testid={`vb-admin-confirm-${booking.id}`} size="sm" onClick={() => onConfirm(note)} className="bg-[#84CC16] hover:bg-[#65A30D] text-black rounded-sm">Confirm</Button>
        <Button data-testid={`vb-admin-reject-${booking.id}`} size="sm" variant="outline" onClick={() => onReject(note)} className="rounded-sm border-[#FF3B30]/40 text-[#FF3B30]">Reject</Button>
        <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setNote(""); }} className="text-neutral-400">Close</Button>
      </div>
    </div>
  );
}

function BookingRow({ booking, role, onPatch }) {
  const { isVendor, isCompanyAdmin, isPlatformAdmin, isPlayer } = role;
  const canVendorAct = isVendor && booking.status === "pending";
  const canAdminAct = isPlatformAdmin && booking.status !== "cancelled";
  // Company admins, organisers, players who OWN this booking can cancel/reschedule
  // — but only while the booking is still active (not terminal, not completed).
  const canBuyerModify = (isCompanyAdmin || isPlayer) && !["cancelled", "rejected", "completed"].includes(booking.status);

  const hrCancel = async () => {
    // Canned reject reasons — small dropdown-via-prompt keeps the code light
    // while giving the buyer a specific cancellation reason. Free-text is
    // accepted via "Other".
    const CODES = [
      { code: "staff_unavailable", label: "Staff unavailable" },
      { code: "maintenance", label: "Under maintenance" },
      { code: "weather", label: "Weather / conditions" },
      { code: "overbooked", label: "Overbooked / double-slot" },
      { code: "customer_request", label: "Customer request" },
      { code: "other", label: "Other (I'll type it)" },
    ];
    const menu = CODES.map((c, i) => `${i + 1}. ${c.label}`).join("\n");
    const pick = window.prompt(`Cancel this booking?\nPick a reason (1–${CODES.length}):\n\n${menu}`);
    if (!pick) return;
    const idx = parseInt(pick, 10) - 1;
    if (isNaN(idx) || idx < 0 || idx >= CODES.length) return;
    const chosen = CODES[idx];
    let text = "";
    if (chosen.code === "other") {
      text = window.prompt("Type the reason (buyer will see this):", "") || "";
      if (!text.trim()) return;
    }
    onPatch(booking.id, {
      __action: "cancel",
      reject_reason_code: chosen.code,
      reject_reason_text: text || chosen.label,
    });
  };
  const hrReschedule = (payload) => {
    onPatch(booking.id, { __action: "reschedule", ...payload });
  };

  return (
    <div data-testid={`vb-row-${booking.id}`} className="border border-white/10 rounded-sm bg-[#141414] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold flex items-center gap-2">
            {booking.listing_title}
            {booking.recurrence_group_id && (
              <span data-testid={`vb-series-${booking.id}`} className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#06B6D4]/20 text-[#06B6D4] border border-[#06B6D4]/40">
                Weekly series
              </span>
            )}
          </div>
          <div className="text-[11px] font-mono text-neutral-500 uppercase tracking-widest mt-0.5">
            {booking.city || "—"} · {booking.sport || booking.vendor_type} · {booking.requested_date} · {booking.start_time}–{booking.end_time} ({booking.hours}h)
          </div>
          {/* Show WHO booked — player name + phone (or HR / organiser). This
              replaces the older "company name" line that confused vendors when
              the actual booker was a player, not a company employee. */}
          {(booking.booker_name || booking.booker_phone) && (
            <div data-testid={`vb-booker-${booking.id}`} className="text-xs font-mono text-neutral-300 mt-1">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 mr-1">
                {booking.booker_role === "company_admin" ? "HR" :
                 booking.booker_role === "organiser" ? "Organiser" :
                 booking.booker_role === "vendor" ? "Vendor (as player)" :
                 booking.booker_role === "sponsor" ? "Sponsor" : "Player"}:
              </span>
              {booking.booker_name || "—"}
              {booking.booker_phone && <span className="text-neutral-500"> · {booking.booker_phone}</span>}
              {booking.booker_role === "company_admin" && booking.company_name && (
                <span className="text-neutral-500"> · {booking.company_name}</span>
              )}
              {booking.offline_referred && (
                <span data-testid={`vb-referred-${booking.id}`} className="ml-2 inline-block text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#F97316]/20 text-[#F97316] border border-[#F97316]/40">
                  Referred · Offline
                </span>
              )}
            </div>
          )}
          {isPlatformAdmin && <div className="text-[11px] font-mono text-neutral-500 mt-0.5">HR: {booking.hr_email || booking.company_name}</div>}
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge booking={booking} />
          <div className="text-right">
            <div className="font-mono text-lg text-[#84CC16]">{fmtPrice(booking.total || booking.price, booking.currency)}</div>
            <div className="text-[10px] font-mono text-neutral-500">{booking.hours}h × {fmtPrice(booking.price, booking.currency)}</div>
          </div>
        </div>
      </div>

      <NotificationBanner booking={booking} />
      {booking.status === "cancelled" && (booking.reject_reason_text || booking.reject_reason_code) && (
        <div data-testid={`vb-reject-reason-${booking.id}`} className="mt-3 border border-[#FF3B30]/40 bg-[#FF3B30]/10 rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF3B30]">/ Cancel reason</div>
          <div className="text-xs text-neutral-200 mt-1">{booking.reject_reason_text || booking.reject_reason_code}</div>
        </div>
      )}
      {booking.status === "completed" && (booking.unused_minutes || 0) > 0 && (
        <div data-testid={`vb-refund-notice-${booking.id}`} className="mt-3 border border-[#FACC15]/40 bg-[#FACC15]/10 rounded-sm p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FACC15]">/ Ended early — refund applied</div>
          <div className="text-xs text-neutral-200 mt-1">
            Unused: {Math.floor(booking.unused_minutes / 60)}h {booking.unused_minutes % 60}m ·
            Refund: {booking.currency || "INR"} {booking.refund_amount || 0}
          </div>
        </div>
      )}
      <RenewalCard booking={booking} onRenewed={() => onPatch && onPatch(booking.id, { __action: "reload" })} />
      {booking.admin_notes && (
        <div className="mt-2 text-[11px] text-[#84CC16] bg-[#84CC16]/5 border border-[#84CC16]/20 rounded-sm px-3 py-2">
          Admin note: {booking.admin_notes}
        </div>
      )}

      {canVendorAct && <VendorActions booking={booking} onAct={(s) => onPatch(booking.id, { status: s })} />}
      {canBuyerModify && <HrCancelReschedule booking={booking} onCancel={hrCancel} onReschedule={hrReschedule} />}
      {(isCompanyAdmin || isPlayer) && booking.status === "completed" && (
        <ReviewForm listingId={booking.listing_id} bookingId={booking.id} />
      )}
      {booking.status === "completed" && (
        <div data-testid={`vb-readonly-${booking.id}`} className="mt-2 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
          / Completed · read-only
        </div>
      )}
      {(booking.refund_amount !== null && booking.refund_amount !== undefined) && (
        <div className="mt-2 text-[11px] text-[#F59E0B] bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-sm px-3 py-2">
          Refund: <span className="font-mono">{fmtPrice(booking.refund_amount, booking.currency)}</span> — {booking.refund_reason}
        </div>
      )}
      {(booking.previous_slots || []).length > 0 && (
        <div className="mt-2 text-[10px] text-neutral-400">
          Rescheduled {booking.reschedule_count}× — last from {booking.previous_slots[booking.previous_slots.length - 1].requested_date} {booking.previous_slots[booking.previous_slots.length - 1].start_time}
        </div>
      )}
      {canAdminAct && (
        <AdminActions
          booking={booking}
          onConfirm={(note) => onPatch(booking.id, { status: "confirmed", admin_notes: note })}
          onReject={(note) => onPatch(booking.id, { status: "rejected", admin_notes: note })}
        />
      )}
    </div>
  );
}

export default function VendorBookings() {
  const role = useAuth();
  const [bookings, reload] = useVendorBookings();
  const [tab, setTab] = useState("active");
  const [query, setQuery] = useState("");

  const onPatch = async (id, payload) => {
    try {
      if (payload.__action === "cancel") {
        await api.post(`/vendor-bookings/${id}/cancel`, {
          notes: payload.notes || "",
          reject_reason_code: payload.reject_reason_code || "",
          reject_reason_text: payload.reject_reason_text || "",
        });
        toast.success("Booking cancelled");
      } else if (payload.__action === "reschedule") {
        const { __action, ...body } = payload; void __action;
        await api.post(`/vendor-bookings/${id}/reschedule`, body);
        toast.success("Booking rescheduled");
      } else if (payload.__action === "reload") {
        // no-op — just refresh (used by RenewalCard after a successful renew)
      } else {
        await api.patch(`/vendor-bookings/${id}`, payload);
        const verb = payload.status?.replace("vendor_", "") || "updated";
        toast.success(`Booking ${verb}`);
      }
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const tabMatchers = {
    all: () => true,
    active: (b) => ["pending", "vendor_accepted", "confirmed"].includes(b.status),
    pending: (b) => ["pending", "vendor_accepted"].includes(b.status),
    approved: (b) => b.status === "confirmed",
    cancelled: (b) => ["cancelled", "rejected", "vendor_declined"].includes(b.status),
    closed: (b) => b.status === "completed",
  };
  const tabCounts = Object.fromEntries(
    Object.keys(tabMatchers).map((k) => [k, bookings.filter(tabMatchers[k]).length])
  );

  const q = query.trim().toLowerCase();
  const filtered = bookings
    .filter(tabMatchers[tab])
    .filter((b) => !q || (b.listing_title || "").toLowerCase().includes(q) || (b.notes || "").toLowerCase().includes(q) || (b.company_name || "").toLowerCase().includes(q));

  if (bookings.length === 0) return null;
  return (
    <div data-testid="vendor-bookings-panel" className="mt-16">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#06B6D4]">/ Ground bookings</div>
      <h2 className="font-display text-3xl tracking-wide mt-2">GROUNDS &amp; TALENT REQUESTS</h2>
      <p className="text-xs text-neutral-500 mt-1">
        {role.isPlatformAdmin && "Confirm or reject each request after coordinating with the vendor."}
        {role.isCompanyAdmin && "Track your booking requests. You'll be notified when Kreeda Nation confirms with the vendor."}
        {role.isPlayer && "Track your venue bookings. Cancel or reschedule while the booking is still active — refund follows the venue's policy."}
        {role.isVendor && "Respond to incoming requests. Kreeda Nation admin will finalize."}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {[
          { k: "active", label: "Active" },
          { k: "pending", label: "Pending" },
          { k: "approved", label: "Approved" },
          { k: "closed", label: "Closed" },
          { k: "cancelled", label: "Cancelled" },
          { k: "all", label: "All" },
        ].map((opt) => (
          <button
            key={opt.k}
            data-testid={`vb-tab-${opt.k}`}
            onClick={() => setTab(opt.k)}
            className={`px-3 py-1.5 text-xs font-mono uppercase rounded-sm border ${
              tab === opt.k
                ? "bg-[#84CC16] border-[#84CC16] text-black"
                : "border-white/10 text-neutral-400 hover:text-white"
            }`}
          >
            {opt.label} · {tabCounts[opt.k]}
          </button>
        ))}
        <div className="ml-auto">
          <Input
            data-testid="vb-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search venue, company, notes…"
            className="bg-black/40 border-white/10 text-white text-sm w-64"
          />
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {filtered.length === 0 ? (
          <div data-testid="vb-empty" className="text-sm text-neutral-500 border border-dashed border-white/10 rounded-sm p-6 text-center">
            No bookings in this tab.
          </div>
        ) : (
          filtered.map((b) => <BookingRow key={b.id} booking={b} role={role} onPatch={onPatch} />)
        )}
      </div>
    </div>
  );
}
